/**
 * A self-contained lighting/material probe scene used *only* by the render
 * team's own shots, so the post pipeline can be judged (and regression-tested)
 * before the level, material and lighting teams land real content.
 *
 * It lives entirely on layer PROBE_LAYER with its own lights, so it is invisible
 * to every other shot and contributes nothing to anyone else's frame. When the
 * real level is in place these shots can simply be pointed at it instead.
 *
 * owner: render
 */
import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';

const _box = new THREE.Box3();
const _c = new THREE.Vector3();
const _dir = new THREE.Vector3();

export const PROBE_LAYER = 7;

/* Viewmodel lighting, expressed as fractions of the *world* rig. These are the
   only free parameters: a carried weapon is partly in the shooter's own shadow
   (VM_KEY_SHADE), gets a whisper of camera-relative fill so it never reads as a
   pure silhouette (VM_FILL, a fraction of the world's ambient), and sees rather
   less than the full sky hemisphere for IBL (VM_IBL). Everything else is copied
   from the world rig verbatim. */
const VM_KEY_SHADE = 0.88;
const VM_FILL = 0.55;
const VM_IBL = 0.85;

function setLayerDeep(obj, layer) {
  obj.traverse((o) => { o.layers.set(layer); });
}

/** Small procedural equirect sky -> PMREM, so indirect specular has something to fall back on. */
function buildEnvironment(renderer) {
  const W = 128, H = 64;
  const data = new Float32Array(W * H * 4);
  const sun = new THREE.Vector3(0.42, 0.55, 0.72).normalize();
  const dir = new THREE.Vector3();
  for (let y = 0; y < H; y++) {
    const theta = (y + 0.5) / H * Math.PI;          // 0 = up
    for (let x = 0; x < W; x++) {
      const phi = (x + 0.5) / W * Math.PI * 2;
      dir.set(Math.sin(theta) * Math.cos(phi), Math.cos(theta), Math.sin(theta) * Math.sin(phi));
      const up = Math.max(0, dir.y);
      // hazy overcast-ish gradient with a soft warm sun disc
      let r = 0.10 + 0.55 * up + 0.30 * (1 - up) * 0.35;
      let g = 0.13 + 0.62 * up + 0.30 * (1 - up) * 0.33;
      let b = 0.19 + 0.80 * up + 0.30 * (1 - up) * 0.30;
      if (dir.y < 0) { r = 0.045; g = 0.042; b = 0.040; }   // ground bounce
      const d = Math.max(0, dir.dot(sun));
      const glow = Math.pow(d, 90) * 26 + Math.pow(d, 8) * 0.55;
      r += glow * 1.0; g += glow * 0.92; b += glow * 0.78;
      const i = (y * W + x) * 4;
      data[i] = r; data[i + 1] = g; data[i + 2] = b; data[i + 3] = 1;
    }
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat, THREE.FloatType);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;

  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const rt = pmrem.fromEquirectangular(tex);
  pmrem.dispose();
  tex.dispose();
  return rt.texture;
}

/**
 * Standing water that is a *surface*, not a mirror polygon.
 *
 * A disc of roughness 0.055 with a hard geometric rim reads exactly as what it
 * is: a flat cut-out of reflected sky pasted onto the road. Two things fix it,
 * and both have to live in the material because SSR keys off roughness:
 *
 *  - a feathered rim. Roughness ramps from mirror at the centre to wetted-but-
 *    not-standing at the edge, which takes it past the SSR roughness cut-off, so
 *    the reflection itself dissolves into damp ground instead of ending on a
 *    knife edge. The albedo lifts toward the road at the same rate.
 *  - ripples. Two decorrelated octaves of world-space wave normal, strongest in
 *    the middle where the water is deepest. Without them the reflection has no
 *    scale and the puddle reads as painted-on sky.
 */
function makeWaterSurface(mat) {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWaterEdge = { value: 0.70 };
    shader.uniforms.uWaterRipple = { value: 0.075 };
    shader.vertexShader = 'varying vec2 vPud;\nvarying vec3 vPudW;\n' + shader.vertexShader.replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
  vPud = uv;
  vPudW = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;`,
    );
    shader.fragmentShader = `
varying vec2 vPud;
varying vec3 vPudW;
uniform float uWaterEdge;
uniform float uWaterRipple;
float wHash(vec2 p){ vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float wNoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(wHash(i), wHash(i + vec2(1,0)), f.x), mix(wHash(i + vec2(0,1)), wHash(i + vec2(1,1)), f.x), f.y);
}
float wFbm(vec2 p){ return wNoise(p) * 0.55 + wNoise(p * 2.31 + 7.1) * 0.28 + wNoise(p * 5.13 + 19.7) * 0.17; }
` + shader.fragmentShader
      .replace('#include <roughnessmap_fragment>', `
  float roughnessFactor = roughness;
  /* An ellipse is not a puddle outline. Warp the rim with the same field that
     drives the ripples so the water ends on a ragged, scalloped boundary the way
     it does against real grit, and ramp roughness only as far as "wetted road",
     not to full dry — pushing it to 0.94 turns the rim into a sheet of blown
     grazing-angle sky, which is a louder artefact than the hard edge was. */
  float pudR = length( vPud - 0.5 ) * 2.0;
  pudR += ( wFbm( vPudW.xz * 2.7 ) - 0.5 ) * 0.42 + ( wFbm( vPudW.xz * 9.0 ) - 0.5 ) * 0.16;
  float pudEdge = smoothstep( uWaterEdge, 1.06, pudR );
  float pudRip = wFbm( vPudW.xz * 5.5 );
  roughnessFactor = mix( roughnessFactor + pudRip * 0.10, 0.58, pudEdge );
`)
      .replace('#include <normal_fragment_begin>', `#include <normal_fragment_begin>
  {
    vec2 wp = vPudW.xz;
    float e = 0.06;
    float h0 = wFbm( wp * 5.5 ) * 0.62 + wFbm( wp * 17.0 + 3.7 ) * 0.38;
    float hx = wFbm( ( wp + vec2( e, 0.0 ) ) * 5.5 ) * 0.62 + wFbm( ( wp + vec2( e, 0.0 ) ) * 17.0 + 3.7 ) * 0.38;
    float hz = wFbm( ( wp + vec2( 0.0, e ) ) * 5.5 ) * 0.62 + wFbm( ( wp + vec2( 0.0, e ) ) * 17.0 + 3.7 ) * 0.38;
    float amp = uWaterRipple * ( 1.0 - smoothstep( uWaterEdge * 0.8, 1.0, length( vPud - 0.5 ) * 2.0 ) );
    vec3 rip = vec3( -( hx - h0 ) / e, 1.0, -( hz - h0 ) / e );
    rip.xz *= amp;
    normal = normalize( mix( normal, normalize( mat3( viewMatrix ) * normalize( rip ) ), 0.85 ) );
  }
`)
      .replace('#include <map_fragment>', `#include <map_fragment>
  {
    float r0 = length( vPud - 0.5 ) * 2.0
             + ( wFbm( vPudW.xz * 2.7 ) - 0.5 ) * 0.42
             + ( wFbm( vPudW.xz * 9.0 ) - 0.5 ) * 0.16;
    diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.048, 0.045, 0.041 ),
                            smoothstep( uWaterEdge, 1.06, r0 ) );
  }
`);
  };
  mat.customProgramCacheKey = () => 'probe-water-1';
  mat.needsUpdate = true;
  return mat;
}

export class ProbeRig {
  constructor() {
    this.root = new THREE.Group();
    this.root.name = 'postfx.probeRig';
    this.root.visible = false;
    this.viewRoot = new THREE.Group();
    this.viewRoot.name = 'postfx.probeViewmodel';
    this.viewRoot.visible = false;
    this.armed = false;
    this.sunDirection = null;
    this.ssrProbe = null;
    this.vmKey = null;
    this.vmFill = null;
    this.env = null;
    this._prevEnv = undefined;
    this._materials = [];
    this._ownViewmodel = false;
  }

  _mat(opts) {
    const m = new THREE.MeshStandardMaterial(opts);
    this._materials.push(m);
    return m;
  }

  build(ctx) {
    const rng = makeRng(0x9a17c0de);
    const root = this.root;

    const asphalt = this._mat({ color: 0x14161a, roughness: 0.17, metalness: 0.0, name: 'probe_wet_asphalt' });
    const concrete = this._mat({ color: 0x4a4740, roughness: 0.88, metalness: 0.0, name: 'probe_concrete' });
    const concreteDark = this._mat({ color: 0x33322e, roughness: 0.94, metalness: 0.0, name: 'probe_concrete_dark' });
    const wood = this._mat({ color: 0x54402a, roughness: 0.78, metalness: 0.0, name: 'probe_wood' });
    const paintedMetal = this._mat({ color: 0x3d4a52, roughness: 0.42, metalness: 0.72, name: 'probe_metal_painted' });
    const steel = this._mat({ color: 0x8d9096, roughness: 0.19, metalness: 1.0, name: 'probe_steel' });
    const rust = this._mat({ color: 0x6a3a22, roughness: 0.72, metalness: 0.35, name: 'probe_rust' });
    const glass = this._mat({ color: 0x090d12, roughness: 0.045, metalness: 0.18, name: 'probe_glass' });
    const lampMat = this._mat({ color: 0x000000, roughness: 1, metalness: 0, emissive: 0xffd9a0, emissiveIntensity: 26, name: 'probe_lamp' });
    const neonMat = this._mat({ color: 0x000000, roughness: 1, metalness: 0, emissive: 0x2fb9ff, emissiveIntensity: 14, name: 'probe_neon' });
    const neonMat2 = this._mat({ color: 0x000000, roughness: 1, metalness: 0, emissive: 0xff5a1e, emissiveIntensity: 11, name: 'probe_neon2' });

    const box = (w, h, d, mat, x, y, z, ry = 0) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      m.rotation.y = ry;
      m.castShadow = true; m.receiveShadow = true;
      root.add(m);
      return m;
    };

    // ground
    const ground = new THREE.Mesh(new THREE.BoxGeometry(80, 0.4, 80), asphalt);
    ground.position.y = -0.2;
    ground.receiveShadow = true;
    root.add(ground);

    // alley shell
    box(26, 7.0, 0.6, concrete, 0, 3.5, -6.4);
    box(0.6, 7.0, 13.0, concreteDark, -8.3, 3.5, 0.0);
    box(0.6, 7.0, 13.0, concrete, 9.2, 3.5, 0.0);
    // overhang: the shadow line and contact occlusion under it is the whole point
    box(11.0, 0.35, 5.2, concreteDark, -3.0, 4.3, -3.6);
    box(0.35, 4.4, 0.35, paintedMetal, 2.2, 2.1, -1.2);
    box(0.35, 4.4, 0.35, paintedMetal, -7.6, 2.1, -1.2);

    // crate / prop cluster in the corner
    box(1.25, 1.25, 1.25, wood, -6.4, 0.63, -4.6, 0.12);
    box(1.25, 1.25, 1.25, wood, -5.05, 0.63, -4.9, -0.06);
    box(1.25, 1.25, 1.25, wood, -6.1, 1.88, -4.75, 0.31);
    box(1.6, 0.9, 1.0, wood, -3.6, 0.45, -5.2, -0.22);
    box(0.9, 1.7, 0.9, rust, -2.2, 0.85, -5.4, 0.4);

    for (let i = 0; i < 5; i++) {
      const r = 0.28 + rng() * 0.06;
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 0.92, 18), i % 2 ? rust : paintedMetal);
      bar.position.set(-7.2 + rng() * 0.5, 0.46, -3.0 + i * 1.05);
      bar.rotation.y = rng() * 3;
      bar.castShadow = bar.receiveShadow = true;
      root.add(bar);
    }

    // wall pipes — long thin contact shadows read strongly in GTAO
    for (let i = 0; i < 3; i++) {
      const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 11.5, 14), steel);
      pipe.rotation.z = Math.PI / 2;
      pipe.position.set(0.5, 4.9 - i * 0.32, -6.0);
      pipe.castShadow = pipe.receiveShadow = true;
      root.add(pipe);
    }

    // polished steel shutter + dark glass storefront (SSR subjects)
    box(4.2, 3.0, 0.12, steel, 5.4, 1.5, -6.0);
    box(3.4, 2.6, 0.1, glass, 1.0, 1.9, -6.02);
    box(3.6, 0.16, 0.3, paintedMetal, 1.0, 0.55, -5.95);

    // hanging lamp + its light
    const lampShade = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.34, 20, 1, true), paintedMetal);
    lampShade.position.set(-2.0, 3.72, -2.6);
    lampShade.rotation.x = Math.PI;
    lampShade.castShadow = false;
    root.add(lampShade);
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.13, 18, 12), lampMat);
    bulb.position.set(-2.0, 3.5, -2.6);
    root.add(bulb);
    const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.55, 6), concreteDark);
    cable.position.set(-2.0, 4.16, -2.6);
    root.add(cable);

    const lamp = new THREE.PointLight(0xffcf94, 26, 22, 2);
    lamp.position.set(-2.0, 3.42, -2.6);
    lamp.castShadow = true;
    lamp.shadow.mapSize.set(1024, 1024);
    lamp.shadow.bias = -0.002;
    lamp.shadow.camera.near = 0.15;
    lamp.shadow.camera.far = 24;
    root.add(lamp);

    // neon signage on the back wall
    box(2.6, 0.3, 0.12, neonMat, 5.4, 3.55, -6.0);
    box(0.22, 1.5, 0.1, neonMat2, 3.5, 2.6, -6.0);

    // sun + ambient, framed on the rig
    const sun = new THREE.DirectionalLight(0xffe9c8, 3.4);
    sun.position.set(11, 15, 14);
    sun.target.position.set(-2, 0, -3);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0009;
    sun.shadow.normalBias = 0.03;
    const sc = sun.shadow.camera;
    sc.left = -16; sc.right = 16; sc.top = 16; sc.bottom = -16; sc.near = 1; sc.far = 70;
    sc.updateProjectionMatrix();
    root.add(sun);
    root.add(sun.target);
    /* The probe scene is lit by its own sun, not the world's. Anything that
       needs a light direction (the contact-shadow march) has to be told which
       one, or the rig's shadows land on the opposite side from its highlights. */
    this.sunDirection = sun.position.clone().sub(sun.target.position).normalize();

    const hemi = new THREE.HemisphereLight(0x8fb6dd, 0x2a2620, 0.55);
    root.add(hemi);

    const bounce = new THREE.DirectionalLight(0x6f86a8, 0.35);
    bounce.position.set(-9, 4, 9);
    root.add(bounce);

    setLayerDeep(root, PROBE_LAYER);
    /* Deliberately NOT added to ctx.scene here — see `arm()`. Staging geometry
       that is in the graph is one stray `visible = true` away from being in
       somebody's hero frame, and that has already happened once (F12). */

    // -------------------------------------------------- placeholder viewmodel
    {
      this._ownViewmodel = true;
      const gunMetal = this._mat({ color: 0x1c1e21, roughness: 0.36, metalness: 0.85, name: 'probe_gun_metal' });
      const polymer = this._mat({ color: 0x24261f, roughness: 0.62, metalness: 0.05, name: 'probe_gun_polymer' });
      const g = this.viewRoot;
      const part = (w, h, d, mat, x, y, z, rx = 0) => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
        m.position.set(x, y, z); m.rotation.x = rx;
        g.add(m);
        return m;
      };
      part(0.055, 0.06, 0.62, gunMetal, 0, 0, -0.30);            // receiver
      part(0.032, 0.032, 0.46, gunMetal, 0, 0.004, -0.74);       // barrel
      part(0.05, 0.10, 0.16, polymer, 0, -0.075, -0.10);         // grip
      part(0.048, 0.13, 0.09, polymer, 0, -0.10, -0.30);         // magazine
      part(0.05, 0.05, 0.24, polymer, 0, -0.005, 0.08);          // stock
      part(0.028, 0.045, 0.05, gunMetal, 0, 0.052, -0.10);       // rear sight
      part(0.02, 0.05, 0.03, gunMetal, 0, 0.052, -0.90);         // front post
      const rail = part(0.05, 0.014, 0.30, gunMetal, 0, 0.042, -0.42);
      rail.castShadow = false;
      g.traverse((o) => { if (o.isMesh) { o.castShadow = false; o.receiveShadow = false; } });

      const key = new THREE.DirectionalLight(0xffe6c4, 2.6);
      key.position.set(0.6, 0.9, 0.4);
      g.add(key);
      const fill = new THREE.HemisphereLight(0x93b4d8, 0x2b2722, 1.1);
      g.add(fill);
      this.vmKey = key;
      this.vmFill = fill;

      // A first-person viewmodel lives in view space. Parent it to the view
      // camera so it tracks the eye exactly like a real weapon rig would.
      g.position.set(0.155, -0.135, -0.02);
      g.rotation.set(0.0, 0.055, 0.0);
      ctx.viewCamera.add(g);
      if (!ctx.viewScene.children.includes(ctx.viewCamera)) ctx.viewScene.add(ctx.viewCamera);
    }
  }

  /**
   * A few sheets of standing water dropped into the *real* level so SSR has a
   * guaranteed subject. Layer 0, lit by the real lighting rig, hidden unless a
   * render shot asks for it.
   */
  buildSsrProbe(ctx) {
    const rng = makeRng(0x51ee7a17);
    const g = new THREE.Group();
    g.name = 'postfx.ssrProbe';
    g.visible = false;
    const water = this._mat({
      color: 0x05070a, roughness: 0.055, metalness: 0.0, name: 'postfx_standing_water',
    });
    water.polygonOffset = true;
    water.polygonOffsetFactor = -2;
    water.polygonOffsetUnits = -2;
    makeWaterSurface(water);

    const puddle = (x, z, rx, rz, rot) => {
      const m = new THREE.Mesh(new THREE.CircleGeometry(1, 40), water);
      m.rotation.x = -Math.PI / 2;
      m.rotation.z = rot;
      m.position.set(x, 0.013, z);
      m.scale.set(rx, rz, 1);
      m.receiveShadow = true;
      m.userData.surface = 'water';
      g.add(m);
    };
    puddle(-1.2, -3.0, 5.4, 3.1, 0.3);
    puddle(2.6, -8.5, 3.6, 2.4, -0.5);
    puddle(-3.9, -10.5, 2.9, 3.6, 0.9);
    puddle(0.9, 2.4, 4.2, 2.2, -0.2);
    puddle(-4.6, 3.6, 2.4, 1.8, 0.6);
    for (let i = 0; i < 7; i++) {
      puddle(rng.range(-7, 7), rng.range(-15, 7), rng.range(0.5, 1.6), rng.range(0.4, 1.2), rng() * 3);
    }
    ctx.scene.add(g);
    this.ssrProbe = g;
  }

  setSsrProbe(on) { if (this.ssrProbe) this.ssrProbe.visible = !!on; }

  /**
   * True when the weapons team's viewmodel is actually in front of the view
   * camera. Until it is, our stand-in stands in — so the viewmodel half of the
   * DOF / motion-vector path can still be seen and regression tested.
   */
  realViewmodelInView(ctx) {
    const root = ctx.weapons && ctx.weapons.viewmodelRoot;
    if (!root || !root.visible) return false;
    _box.makeEmpty();
    _box.setFromObject(root);
    if (_box.isEmpty()) return false;
    _box.getCenter(_c);
    return _c.distanceTo(ctx.viewCamera.position) < 3.0;
  }

  /**
   * Put everything in `ctx.viewScene` on the same absolute lighting scale as
   * `ctx.scene`.
   *
   * This is the second half of FINDINGS F1. The world is lit in scene units
   * derived from real photometry (see CONTRACTS.md :: Scene units); the weapon
   * viewmodel rig was authored against hand-picked numbers and ended up roughly
   * 100x hotter than the street, so auto-exposure had to choose between a black
   * world and a white gun. Nothing here changes the *ratios* the weapons team
   * authored between their key, fills and IBL — only the absolute scale, which
   * is not theirs to choose.
   *
   * It runs from the render system's update (priority 1000), i.e. after every
   * other system has had its say, so it is always the last word on the frame
   * that is about to be drawn.
   */
  syncViewmodelLights(ctx) {
    const L = ctx.lighting;
    const sun = L && L.sun;

    // --- the render team's own stand-in viewmodel
    if (this.vmKey && sun && sun.intensity > 0) {
      this.vmKey.intensity = sun.intensity * VM_KEY_SHADE;
      this.vmKey.color.copy(sun.color);
      this.vmFill.intensity = (L.skyFill ? L.skyFill.intensity : sun.intensity * 0.01) * 1.0;
    }

    // --- the weapons team's real viewmodel rig
    if (!sun || !(sun.intensity > 0)) return;
    const skyFill = L.skyFill;
    const bounce = L.bounce;
    const ambient = (skyFill ? skyFill.intensity : 0) + (bounce ? bounce.intensity : 0);
    const sunDir = ctx.sky ? ctx.sky.sunDirection : null;

    const scene = ctx.viewScene;
    const children = scene.children;
    for (let i = 0; i < children.length; i++) {
      const o = children[i];
      // The world-aligned trio the weapons team parents directly to viewScene.
      if (o.isDirectionalLight) {
        _dir.copy(o.position);
        if (o.target) _dir.sub(o.target.position);
        if (_dir.lengthSq() < 1e-8) continue;
        _dir.normalize();
        if (sunDir && _dir.dot(sunDir) > 0.86) {
          o.intensity = sun.intensity * VM_KEY_SHADE;
          o.color.copy(sun.color);
        } else if (_dir.y > 0.4) {
          o.intensity = skyFill ? skyFill.intensity : ambient * 0.7;
          if (skyFill) o.color.copy(skyFill.color);
        } else if (_dir.y < -0.4) {
          o.intensity = bounce ? bounce.intensity : ambient * 0.3;
          if (bounce) o.color.copy(bounce.color);
        } else {
          o.intensity = ambient * VM_FILL;
        }
      } else if (o.isHemisphereLight) {
        o.intensity = skyFill ? skyFill.intensity : ambient;
        if (skyFill) { o.color.copy(skyFill.color); o.groundColor.copy(skyFill.groundColor); }
      }
    }

    // Camera-relative readability fills: parented under the viewmodel root, not
    // under viewScene, so they are found by walking it rather than by index.
    const vmRoot = ctx.weapons && ctx.weapons.viewmodelRoot;
    if (vmRoot) {
      let n = 0;
      vmRoot.traverse((o) => {
        if (!o.isLight || o.isPointLight) return;
        o.intensity = ambient * (n === 0 ? VM_FILL : VM_FILL * 0.45);
        n++;
      });
    }

    // A held weapon sees most of the sky, so it takes the world's IBL at the
    // world's strength. The 1.6x boost that used to live here was a symptom of
    // the same scale mismatch.
    if (ctx.scene.environment) {
      if (ctx.viewScene.environment !== ctx.scene.environment) {
        ctx.viewScene.environment = ctx.scene.environment;
      }
      ctx.viewScene.environmentIntensity = (ctx.scene.environmentIntensity ?? 1) * VM_IBL;
    }
  }

  /** Called once the renderer exists (PMREM needs it). */
  ensureEnv(renderer) {
    if (this.env) return this.env;
    try { this.env = buildEnvironment(renderer); } catch (e) { this.env = null; }
    return this.env;
  }

  /**
   * Arm the rig.
   *
   * Two things here are not cosmetic:
   *
   * 1. The root is *attached* to the scene here and detached in `disarm`, not
   *    merely shown and hidden. These are staging objects and they have leaked
   *    into shipped frames before (FINDINGS F12: `probe_neon`, an emissive
   *    #2fb9ff box, appeared in the `light_shafts` ceiling and was reported by
   *    a reviewer as a level defect). A `visible` flag is one stray traversal
   *    away from being true again — `main.js :: restoreVisibility` walks the
   *    whole graph every shot, and `resetTransient` calls
   *    `camera.layers.enableAll()`, which re-enables PROBE_LAYER. An object
   *    that is not in the scene cannot be drawn by any of that.
   *
   *    Note it is NOT gated on `ctx.config.debug`: the capture harness loads
   *    the page with `?debug`, so `ctx.config.debug` is *true* during every
   *    screenshot this project has ever taken. Gating on it would have looked
   *    like a fix and changed nothing. Attachment is the gate.
   *
   * 2. The rig's sun and lamp are shadow casters that come into existence
   *    mid-run while `renderer.shadowMap.autoUpdate === false`. three uploads
   *    `null` for a caster whose map has never been rendered, `setValueT1Array`
   *    substitutes its 1x1 RGBA placeholder, and a plain RGBA texture in a
   *    `sampler2DShadow` is `GL_INVALID_OPERATION` on ANGLE — which *discards
   *    the draw*. Forcing one shadow update on the frame the lights appear is
   *    the whole fix.
   */
  arm(ctx) {
    this.armed = true;
    if (this.root.parent !== ctx.scene) ctx.scene.add(this.root);
    this.root.visible = true;
    if (this._ownViewmodel) this.viewRoot.visible = true;
    ctx.camera.layers.enable(PROBE_LAYER);
    ctx.camera.layers.disable(0);
    if (ctx.renderer) ctx.renderer.shadowMap.needsUpdate = true;
    if (this.env && ctx.scene.environment !== this.env) {
      if (this._prevEnv === undefined) this._prevEnv = ctx.scene.environment;
      ctx.scene.environment = this.env;
      ctx.scene.environmentIntensity = 1.0;
      if (!ctx.viewScene.environment) ctx.viewScene.environment = this.env;
    }
  }

  disarm(ctx) {
    if (!this.armed) return;
    this.armed = false;
    this.root.visible = false;
    this.viewRoot.visible = false;
    this.root.removeFromParent();
    ctx.camera.layers.enable(0);
    ctx.camera.layers.disable(PROBE_LAYER);
    if (this._prevEnv !== undefined) {
      ctx.scene.environment = this._prevEnv;
      this._prevEnv = undefined;
    }
  }

  dispose() {
    if (this.ssrProbe) this.ssrProbe.removeFromParent();
    this.root.removeFromParent();
    this.viewRoot.removeFromParent();
    for (const m of this._materials) m.dispose();
    this.root.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
    this.viewRoot.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
  }
}
